import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { stripWhitespace } from "../utils/cleaning";

export type VendorUpdateFormFields = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  birthMonth: string;
  birthDay: string;
};

export type VendorUpdateDialogProps = {
  open: boolean;
  vendorName: string;
  loading: boolean;
  initialValues: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    dateOfBirth?: string;
  };
  onConfirm: (values: VendorUpdateFormFields) => void;
  onCancel: () => void;
  /** Optional: hide birthdate fields (some vendors may not support it) */
  hideBirthDate?: boolean;
};

/**
 * Parse birthdate string into month and day components
 */
const parseBirthDate = (dateOfBirth?: string): { month: string; day: string } => {
  if (!dateOfBirth) {
    return { month: "", day: "" };
  }

  // Handle YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}/.test(dateOfBirth)) {
    const parts = dateOfBirth.split("-");
    return { month: parts[1], day: parts[2].substring(0, 2) };
  }

  // Handle MM-DD format
  if (/^\d{2}-\d{2}$/.test(dateOfBirth)) {
    const [month, day] = dateOfBirth.split("-");
    return { month, day };
  }

  // Handle MM/DD format
  if (/^\d{2}\/\d{2}$/.test(dateOfBirth)) {
    const [month, day] = dateOfBirth.split("/");
    return { month, day };
  }

  return { month: "", day: "" };
};

export const VendorUpdateDialog: React.FC<VendorUpdateDialogProps> = ({
  open,
  vendorName,
  loading,
  initialValues,
  onConfirm,
  onCancel,
  hideBirthDate = false,
}) => {
  const [formFields, setFormFields] = useState<VendorUpdateFormFields>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    birthMonth: "",
    birthDay: "",
  });

  // Reset form fields when dialog opens or initial values change
  const resetFormFields = useCallback(() => {
    const { month, day } = parseBirthDate(initialValues?.dateOfBirth);
    setFormFields({
      firstName: initialValues?.firstName ?? "",
      lastName: initialValues?.lastName ?? "",
      email: initialValues?.email ?? "",
      phoneNumber: initialValues?.phoneNumber ?? "",
      birthMonth: month,
      birthDay: day,
    });
  }, [initialValues]);

  useEffect(() => {
    if (open) {
      resetFormFields();
    }
  }, [open, resetFormFields]);

  const handleFieldChange = (field: keyof VendorUpdateFormFields) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Phone fields never accept whitespace — strip it on every keystroke.
    const nextValue =
      field === "phoneNumber"
        ? stripWhitespace(event.target.value)
        : event.target.value;
    setFormFields((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  };

  const handleConfirm = () => {
    onConfirm(formFields);
  };

  const handleClose = () => {
    if (!loading) {
      resetFormFields();
      onCancel();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update {vendorName} Account</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formFields.firstName}
                onChange={handleFieldChange("firstName")}
                disabled={loading}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formFields.lastName}
                onChange={handleFieldChange("lastName")}
                disabled={loading}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formFields.email}
                onChange={handleFieldChange("email")}
                disabled={loading}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={hideBirthDate ? 12 : 6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formFields.phoneNumber}
                onChange={handleFieldChange("phoneNumber")}
                disabled={loading}
                size="small"
                variant="outlined"
                placeholder="(555) 555-5555"
              />
            </Grid>
            {!hideBirthDate && (
              <>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small" disabled={loading}>
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={formFields.birthMonth}
                      label="Month"
                      onChange={(e) =>
                        setFormFields((prev) => ({
                          ...prev,
                          birthMonth: e.target.value,
                        }))
                      }
                    >
                      <MenuItem value="">--</MenuItem>
                      <MenuItem value="01">January</MenuItem>
                      <MenuItem value="02">February</MenuItem>
                      <MenuItem value="03">March</MenuItem>
                      <MenuItem value="04">April</MenuItem>
                      <MenuItem value="05">May</MenuItem>
                      <MenuItem value="06">June</MenuItem>
                      <MenuItem value="07">July</MenuItem>
                      <MenuItem value="08">August</MenuItem>
                      <MenuItem value="09">September</MenuItem>
                      <MenuItem value="10">October</MenuItem>
                      <MenuItem value="11">November</MenuItem>
                      <MenuItem value="12">December</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small" disabled={loading}>
                    <InputLabel>Day</InputLabel>
                    <Select
                      value={formFields.birthDay}
                      label="Day"
                      onChange={(e) =>
                        setFormFields((prev) => ({
                          ...prev,
                          birthDay: e.target.value,
                        }))
                      }
                    >
                      <MenuItem value="">--</MenuItem>
                      {Array.from({ length: 31 }, (_, i) => {
                        const day = String(i + 1).padStart(2, "0");
                        return (
                          <MenuItem key={day} value={day}>
                            {i + 1}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{
            color: "#041E42",
            borderColor: "#041E42",
            "&:hover": {
              borderColor: "#0a3d7a",
              backgroundColor: "rgba(4, 30, 66, 0.04)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? <CircularProgress size={20} /> : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorUpdateDialog;
